import { useState } from "react";

import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaInfoCircle } from "react-icons/fa";

import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { ResumeIndicatorB1ByHealthTeamData } from "@/services/esus/indicator/esb/b1/getResumeIndicatorB1ByHealthTeam";
import {
	getIndicatorClassification,
	getIndicatorClassificationColorClassName,
	getIndicatorClassificationScores,
	IndicatorEsbEnum,
} from "@/utils/indicatorsEsb";
import { calculatePercentage } from "@/utils/calculatePercentage";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEsbB1HealthTeamsClassificationTableColumns = {
	mainHealthTeamName: Column;
	linkedHealthTeamName: Column;
	totalCitizensFinishedLinked: Column;
	totalCitizensFinishedNotLinked: Column;
	totalCitizens: Column;
	classificationPercentageLinked: Column;
	classificationLinked: Column;
	classificationPercentage: Column;
	classification: Column;
};

type IndicatorEsbB1HealthTeamsTableProps = {
	healthTeamsClassifications: ResumeIndicatorB1ByHealthTeamData[];
	isLoading: boolean;
};

const indicatorClassificationScores = getIndicatorClassificationScores(IndicatorEsbEnum.B1);

export function IndicatorEsbB1HealthTeamsClassificationTable({
	healthTeamsClassifications,
	isLoading,
}: Readonly<IndicatorEsbB1HealthTeamsTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsbB1HealthTeamsClassificationTableColumns>({
		mainHealthTeamName: { title: "Equipe ESB", visible: true },
		linkedHealthTeamName: { title: "Equipe ESF", visible: true },
		totalCitizensFinishedLinked: { title: "Cidadãos vinculados atendidos", visible: true },
		totalCitizensFinishedNotLinked: {
			title: "Cidadãos não vinculados atendidos",
			visible: true,
		},
		totalCitizens: { title: "Cidadãos vinculados", visible: true },
		classificationPercentageLinked: { title: "% Classificação cidadãos vinculados", visible: false },
		classificationLinked: { title: "Classificação cidadãos vinculados", visible: false },
		classificationPercentage: { title: "% Classificação", visible: true },
		classification: { title: "Classificação", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th
						className={`text-start px-2 ${
							!columns.mainHealthTeamName.visible && "d-none"
						}`}
					>
						{columns.mainHealthTeamName.title}
					</th>
					<th
						className={`text-start px-2 ${
							!columns.linkedHealthTeamName.visible && "d-none"
						}`}
					>
						{columns.linkedHealthTeamName.title}
					</th>
					<th
						className={`px-2 ${
							!columns.totalCitizensFinishedLinked.visible && "d-none"
						}`}
					>
						{columns.totalCitizensFinishedLinked.title}
					</th>
					<th
						className={`px-2 ${
							!columns.totalCitizensFinishedNotLinked.visible && "d-none"
						}`}
					>
						{columns.totalCitizensFinishedNotLinked.title}
					</th>
					<th className={`px-2 ${!columns.totalCitizens.visible && "d-none"}`}>
						{columns.totalCitizens.title}
					</th>
					<th className={`px-2 ${!columns.classificationPercentageLinked.visible && "d-none"}`}>
						{columns.classificationPercentageLinked.title}{" "}
						<CardOverlayTrigger header={"Classificação"} icon={<FaInfoCircle />}>
							<div className="text-start">
								Percentual de cidadãos vinculados atendidos <br />
								sobre a quantidade de cidadãos vinculados
							</div>
						</CardOverlayTrigger>
					</th>
					<th className={`px-2 ${!columns.classificationLinked.visible && "d-none"}`}>
						{columns.classificationLinked.title}{" "}
						<CardOverlayTrigger header={"Classificação"} icon={<FaInfoCircle />}>
							<div className="text-start">
								<div className="text-primary">{`Ótimo: > ${indicatorClassificationScores.awesome}% e <= ${indicatorClassificationScores.limit}%`}</div>
								<br />
								<div className="text-success">{`Bom: > ${indicatorClassificationScores.good}% e <= ${indicatorClassificationScores.awesome}%`}</div>
								<br />
								<div className="text-warning">
									{`Suficiente: > ${indicatorClassificationScores.enough}% e <= ${indicatorClassificationScores.good}%`}
								</div>
								<br />
								<div className="text-danger">{`Regular: <= ${indicatorClassificationScores.enough}%`}</div>
								<br />
							</div>
						</CardOverlayTrigger>
					</th>
					<th className={`px-2 ${!columns.classificationPercentage.visible && "d-none"}`}>
						{columns.classificationPercentage.title}{" "}
						<CardOverlayTrigger header={"Classificação"} icon={<FaInfoCircle />}>
							<div className="text-start">
								Percentual de cidadãos vinculados e não vinculados atendidos <br />
								sobre a quantidade de cidadãos vinculados
							</div>
						</CardOverlayTrigger>
					</th>
					<th className={`px-2 ${!columns.classification.visible && "d-none"}`}>
						{columns.classification.title}{" "}
						<CardOverlayTrigger header={"Classificação"} icon={<FaInfoCircle />}>
							<div className="text-start">
								<div className="text-primary">{`Ótimo: > ${indicatorClassificationScores.awesome}% e <= ${indicatorClassificationScores.limit}%`}</div>
								<br />
								<div className="text-success">{`Bom: > ${indicatorClassificationScores.good}% e <= ${indicatorClassificationScores.awesome}%`}</div>
								<br />
								<div className="text-warning">
									{`Suficiente: > ${indicatorClassificationScores.enough}% e <= ${indicatorClassificationScores.good}%`}
								</div>
								<br />
								<div className="text-danger">{`Regular: <= ${indicatorClassificationScores.enough}%`}</div>
								<br />
							</div>
						</CardOverlayTrigger>
					</th>
					<th className="text-nowrap pb-0">
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsbB1HealthTeamsClassificationTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsbB1HealthTeamsClassificationTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsbB1HealthTeamsClassificationTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && healthTeamsClassifications.length > 0
					? healthTeamsClassifications.map((healthTeamClassification, index) => (
							<tr
								className="text-center"
								key={`health-team-classification-table-row-${index + 1}`}
							>
								<td
									className={`text-start px-2 ${
										!columns.mainHealthTeamName.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>{healthTeamClassification.esbHealthTeamName}</div>
										<div className="text-muted">
											{healthTeamClassification.esbHealthTeamIne}
										</div>
									</div>
								</td>
								<td
									className={`text-start px-2 ${
										!columns.linkedHealthTeamName.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>{healthTeamClassification.linkedHealthTeamName}</div>
										<div className="text-muted">
											{healthTeamClassification.linkedHealthTeamIne}
										</div>
									</div>
								</td>
								<td
									className={`px-2 ${
										!columns.totalCitizensFinishedLinked.visible && "d-none"
									}`}
								>
									{healthTeamClassification.totalCitizensFinishedLinked ?? 0}
								</td>
								<td
									className={`px-2 ${
										!columns.totalCitizensFinishedNotLinked.visible && "d-none"
									}`}
								>
									{(healthTeamClassification.totalCitizensFinished ?? 0) -
										(healthTeamClassification.totalCitizensFinishedLinked ?? 0)}
								</td>
								<td
									className={`px-2 ${!columns.totalCitizens.visible && "d-none"}`}
								>
									{healthTeamClassification.totalCitizens ?? 0}
								</td>
								<td
									className={`px-2 ${
										!columns.classificationPercentageLinked.visible && "d-none"
									}`}
								>
									{`${calculatePercentage(
										healthTeamClassification.totalCitizensFinishedLinked,
										healthTeamClassification.totalCitizens
									)}%`}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.classificationLinked.visible && "d-none"
									}`}
								>
									<Badge
										bg={getIndicatorClassificationColorClassName(
											IndicatorEsbEnum.B1,
											calculatePercentage(
												healthTeamClassification.totalCitizensFinishedLinked,
												healthTeamClassification.totalCitizens
											)
										)}
									>
										{getIndicatorClassification(
											IndicatorEsbEnum.B1,
											calculatePercentage(
												healthTeamClassification.totalCitizensFinishedLinked,
												healthTeamClassification.totalCitizens
											)
										)}
									</Badge>
								</td>
								<td
									className={`px-2 ${
										!columns.classificationPercentage.visible && "d-none"
									}`}
								>
									{`${calculatePercentage(
										healthTeamClassification.totalCitizensFinished,
										healthTeamClassification.totalCitizens
									)}%`}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.classification.visible && "d-none"
									}`}
								>
									<Badge
										bg={getIndicatorClassificationColorClassName(
											IndicatorEsbEnum.B1,
											calculatePercentage(
												healthTeamClassification.totalCitizensFinished,
												healthTeamClassification.totalCitizens
											)
										)}
									>
										{getIndicatorClassification(
											IndicatorEsbEnum.B1,
											calculatePercentage(
												healthTeamClassification.totalCitizensFinished,
												healthTeamClassification.totalCitizens
											)
										)}
									</Badge>
								</td>
								<td></td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
